import { CircleCheckIcon } from "lucide-react";
import React from "react";

import * as z from "zod";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { cn } from "@/components/lib/css";

type InputAttributes = React.InputHTMLAttributes<HTMLInputElement>;

type ZodObject = z.ZodObject<z.ZodRawShape>;

export type InputProps = InputAttributes &
  Required<Pick<InputAttributes, "placeholder">> & {
    schema?: ZodObject;
    maskMe?: boolean;
  };

const InputLabelAnimation = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = "text", schema, maskMe, ...props }, ref) => {
    const field = schema?.shape?.[props?.name || ""] as
      | z.ZodTypeAny
      | undefined;
    const value = props?.value;
    const hasValue = Array.isArray(value)
      ? value.length > 0
      : value !== undefined && value !== "";
    const isReady = (hasValue && field?.safeParse(value)?.success) || false;

    // Question: Should we add some hook into the parent styles?
    // get aria props
    const ariaProps = Object.keys(props).reduce(
      (acc, key) => {
        if (key.startsWith("aria-")) {
          // @ts-expect-error key is a string
          acc[key] = props[key];
        }
        return acc;
      },
      {} as Record<string, string>
    );

    return (
      <div className="group relative w-full" {...ariaProps}>
        <Label
          htmlFor={props.id}
          className={cn(
            "text-color-text-dimmed absolute top-1/2 block -translate-y-1/2 cursor-text px-1 text-sm/[14px] transition-all",
            // Group Focus
            "group-focus-within:pointer-events-none group-focus-within:top-2 group-focus-within:translate-y-0",
            "group-focus-within:cursor-default group-focus-within:text-[10px]/[10px] group-focus-within:font-medium",
            // Input Placeholder
            "has-[+input:not(:placeholder-shown)]:pointer-events-none has-[+input:not(:placeholder-shown)]:top-2",
            "has-[+input:not(:placeholder-shown)]:translate-y-0 has-[+input:not(:placeholder-shown)]:cursor-default",
            "has-[+input:not(:placeholder-shown)]:text-[10px]/[10px] has-[+input:not(:placeholder-shown)]:font-medium",
            // Group aria-invalid
            "group-aria-[invalid=true]:text-color-accent-negative"
          )}
        >
          <span className="inline-flex px-2">{props.placeholder}</span>
        </Label>
        <Input
          className={cn("pr-11", className)}
          ref={ref}
          type={type}
          {...props}
          // Exclude showing placeholder on <input/> since we are using the placeholder in the <label/> element above
          placeholder=""
          maskMe={maskMe}
        />
        {isReady ? (
          <div className="absolute top-0 right-0 bottom-0 flex items-center justify-center p-3">
            <CircleCheckIcon className="text-color-accent-affirmative h-6 w-6" />
          </div>
        ) : null}
      </div>
    );
  }
);
InputLabelAnimation.displayName = "InputLabelAnimation";

export { InputLabelAnimation };
